import { useEffect } from 'react'

import type { MedioPagoPos } from './tipos'

/**
 * Engancha la tecla rápida de cada medio de pago.
 *
 * El atajo se venía MOSTRANDO en los dos diálogos de cobro y en la grilla de
 * configuración, y nadie lo escuchaba: el POS decía «F5» abajo de Efectivo y
 * apretar F5 no hacía nada. Es el mismo error que un parámetro sembrado que
 * ningún código lee, y acá es peor que inútil: el cartel le promete al cajero
 * un atajo que no existe, y la razón de ser del campo es justamente que cobrar
 * en efectivo sea UNA tecla.
 *
 * Se escucha en `window` y no en el diálogo porque el foco arranca en el campo
 * del importe: un handler en el contenedor no vería la tecla. Y por eso mismo
 * hay que dejar pasar lo que el usuario está TIPEANDO —si no, elegir un medio
 * con tecla «5» haría imposible escribir un importe con un cinco—, así que un
 * atajo de un solo caracter se ignora mientras el foco está en un campo de
 * texto. Las de función (F2 a F12) valen siempre: no se tipean.
 *
 * F1 no se puede asignar (lo valida el servidor): es la ayuda, y en el
 * mostrador la ayuda del diálogo de cobro es lo que hace falta cuando algo no
 * se entiende.
 */
export function useTeclaDelMedio(
  medios: MedioPagoPos[],
  onElegir: (medio: MedioPagoPos) => void,
) {
  useEffect(() => {
    function alApretar(e: KeyboardEvent) {
      // Con un modificador es un atajo del navegador o del sistema.
      if (e.ctrlKey || e.altKey || e.metaKey) return

      const tecla = e.key.toUpperCase()

      const medio = medios.find(
        (m) => (m.teclaRapida ?? '').trim().toUpperCase() === tecla,
      )

      if (!medio) return

      const esDeFuncion = /^F([2-9]|1[0-2])$/.test(tecla)

      if (!esDeFuncion) {
        const activo = document.activeElement
        const tipeando =
          activo instanceof HTMLInputElement ||
          activo instanceof HTMLTextAreaElement ||
          (activo instanceof HTMLElement && activo.isContentEditable)

        if (tipeando) return
      }

      e.preventDefault()
      onElegir(medio)
    }

    window.addEventListener('keydown', alApretar)
    return () => window.removeEventListener('keydown', alApretar)
  }, [medios, onElegir])
}
